import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TagModule } from 'primeng/tag';
import {
  EmailAuditDTO,
  EmailAuditStatus,
  SUPPRESSED_EMAIL_TOOLTIP
} from '../../services/email-audit.service';

@Component({
  selector: 'app-email-audit-read',
  templateUrl: './email-audit-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      .email-body-preview {
        display: block;
        width: 100%;
        min-height: 50vh;
        border: 0;
        background: white;
      }
    `
  ],
  imports: [CommonModule, TagModule]
})
export class EmailAuditReadComponent {
  email = input.required<EmailAuditDTO>();
  openCustomer = output<number>();
  openSale = output<number>();
  openSubscription = output<number>();
  openSaleLink = output<number>();

  private sanitizer = inject(DomSanitizer);

  statusLabel(status?: EmailAuditStatus): string {
    switch (status) {
      case 'SENT':
        return 'Enviado';
      case 'FAILED':
        return 'Fallido';
      case 'PENDING':
        return 'Pendiente';
      case 'SUPPRESSED':
        return 'Suprimido';
      default:
        return status ?? '—';
    }
  }

  statusTooltip(status?: EmailAuditStatus): string | undefined {
    return status === 'SUPPRESSED' ? SUPPRESSED_EMAIL_TOOLTIP : undefined;
  }

  statusSeverity(status?: EmailAuditStatus): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (status) {
      case 'SENT':
        return 'success';
      case 'FAILED':
        return 'danger';
      case 'PENDING':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  customerDisplayName(email: EmailAuditDTO): string {
    return email.customerName?.trim() || email.customerEmail?.trim() || '';
  }

  onOpenCustomer(email: EmailAuditDTO): void {
    if (email.customerId != null) {
      this.openCustomer.emit(email.customerId);
    }
  }

  onOpenSale(email: EmailAuditDTO): void {
    if (email.saleId != null) {
      this.openSale.emit(email.saleId);
    }
  }

  onOpenSubscription(email: EmailAuditDTO): void {
    if (email.subscriptionId != null) {
      this.openSubscription.emit(email.subscriptionId);
    }
  }

  onOpenSaleLink(email: EmailAuditDTO): void {
    if (email.saleLinkId != null) {
      this.openSaleLink.emit(email.saleLinkId);
    }
  }

  formatFileSize(size?: number): string {
    if (size == null || !Number.isFinite(size)) {
      return '';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  isOpened(email: EmailAuditDTO): boolean {
    return email.status === 'SENT' && !!email.openedAt;
  }

  openedLabel(email: EmailAuditDTO): string {
    if (email.status !== 'SENT') {
      return '—';
    }
    return this.isOpened(email) ? 'Abierto' : 'No abierto';
  }

  openedSeverity(email: EmailAuditDTO): 'success' | 'secondary' {
    return this.isOpened(email) ? 'success' : 'secondary';
  }

  emailBodySrcdoc(body?: string | null): SafeHtml | null {
    if (!body?.trim()) {
      return null;
    }
    const previewBody = body.replaceAll(
      '/public/email-audit/open?',
      '/public/email-audit/open?skipView=true&'
    );
    return this.sanitizer.bypassSecurityTrustHtml(previewBody);
  }
}
